import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Send,
  Lock,
  Star,
  ClipboardList,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

const DEPARTMENTS = [
  { value: 'production', label: 'Production' },
  { value: 'sound', label: 'Sound' },
  { value: 'lighting', label: 'Lighting & Visuals' },
  { value: 'backline_stage', label: 'Backline & Stage' },
  { value: 'artist_band', label: 'Artist & Band' },
  { value: 'wardrobe_personal', label: 'Wardrobe & Personal' },
  { value: 'management', label: 'Management' },
  { value: 'venue_promoter', label: 'Venue & Promoter' },
  { value: 'logistics', label: 'Logistics & Transport' },
  { value: 'all_departments', label: 'All Departments' },
] as const

interface ActionItem {
  id: string
  description: string
  assignedTo: string | null
  dueBy: string | null
  resolved: boolean
  resolvedAt: string | null
}

interface DebriefSection {
  id: string
  department: string
  writtenBy: string | null
  ratingOverall: number | null
  ratingVenue: number | null
  ratingSound: number | null
  ratingLighting: number | null
  ratingLoadIn: number | null
  ratingCatering: number | null
  notes: string | null
  keyLearnings: string | null
  avoidNext: string | null
  isConfidential: boolean
  actionItems: ActionItem[]
}

interface DebriefRow {
  id: string
  showId: string
  createdBy: string
  status: string
  publishedAt: string | null
  overallNotes: string | null
  createdAt: string
  sections: DebriefSection[]
  show?: { id: string; title: string; date: string; venue: string }
}

function StarRatingInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(value === i ? null : i)}
          className={`text-lg leading-none ${i <= (value ?? 0) ? 'text-yellow-500' : 'text-gray-300'} hover:text-yellow-400`}
        >
          <Star className="h-4 w-4" fill={i <= (value ?? 0) ? 'currentColor' : 'none'} />
        </button>
      ))}
    </div>
  )
}

function StarRatingDisplay({ value }: { value: number | null }) {
  if (!value) return <span className="text-xs text-ovation-muted">--</span>
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3 w-3 ${i <= value ? 'text-yellow-500' : 'text-gray-200'}`}
          fill={i <= value ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  )
}

function getDeptLabel(value: string): string {
  return DEPARTMENTS.find((d) => d.value === value)?.label ?? value
}

export function DebriefPage() {
  const qc = useQueryClient()
  const scope = usePerformanceScope()
  const [adding, setAdding] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string>('production')

  const { data, error, isLoading } = useQuery<DebriefRow[], ApiClientError>({
    queryKey: ['debriefs', ...scope.queryKeySuffix],
    queryFn: () => artistFetch(`/debriefs${scope.toQueryString()}`),
  })

  const debriefs = scope.filterByShowIds(data ?? [], (r) => r.showId)

  const publishMutation = useMutation({
    mutationFn: (id: string) => artistFetch(`/debriefs/${id}/publish`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debriefs'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => artistFetch(`/debriefs/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debriefs'] }),
  })

  return (
    <div>
      <PageHeader
        title="Post-show Debriefs"
        subtitle="Departmental debriefs with ratings, action items, and institutional memory."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'New debrief'}
          </button>
        }
      />

      <PerformanceScopeBanner />

      {adding && (
        <AddDebriefForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['debriefs'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        <div className="space-y-3">
          {debriefs.length === 0 && (
            <p className="py-8 text-center text-sm text-ovation-muted">No debriefs recorded yet.</p>
          )}
          {debriefs.map((debrief) => (
            <div key={debrief.id} className="rounded-lg border border-ovation-ink/10 bg-white">
              <div
                className="flex cursor-pointer items-center gap-3 px-4 py-3"
                onClick={() => setExpanded(expanded === debrief.id ? null : debrief.id)}
              >
                {expanded === debrief.id ? (
                  <ChevronDown className="h-4 w-4 text-ovation-muted" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-ovation-muted" />
                )}
                <ClipboardList className="h-4 w-4 text-ovation-muted" />
                <div className="flex-1">
                  <span className="font-medium">
                    {debrief.show?.title ?? debrief.showId.slice(0, 8)}
                  </span>
                  {debrief.show?.venue && (
                    <span className="ml-2 text-xs text-ovation-muted">{debrief.show.venue}</span>
                  )}
                  {debrief.show?.date && (
                    <span className="ml-2 text-xs text-ovation-muted">
                      {new Date(debrief.show.date).toLocaleDateString()}
                    </span>
                  )}
                  <span className="ml-2 text-xs text-ovation-muted">
                    {debrief.sections.filter((s) => s.notes).length}/{debrief.sections.length} sections filled
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {debrief.status === 'published' ? (
                    <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      <CheckCircle className="h-3 w-3" /> Published
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => publishMutation.mutate(debrief.id)}
                      className="flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
                    >
                      <Send className="h-3 w-3" /> Publish
                    </button>
                  )}
                  {debrief.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(debrief.id)}
                      className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {expanded === debrief.id && (
                <div className="border-t border-ovation-ink/5">
                  {/* Overall notes */}
                  {debrief.overallNotes && (
                    <div className="border-b border-ovation-ink/5 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wider text-ovation-muted">Overall Notes</p>
                      <p className="mt-1 text-sm whitespace-pre-wrap">{debrief.overallNotes}</p>
                    </div>
                  )}

                  {/* Department tabs */}
                  <div className="flex overflow-x-auto border-b border-ovation-ink/5">
                    {debrief.sections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => setActiveTab(section.department)}
                        className={`shrink-0 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                          activeTab === section.department
                            ? 'border-ovation-ink text-ovation-ink'
                            : 'border-transparent text-ovation-muted hover:text-ovation-ink/70'
                        } ${section.notes ? '' : 'opacity-50'}`}
                      >
                        <span className="flex items-center gap-1">
                          {getDeptLabel(section.department)}
                          {section.isConfidential && <Lock className="h-3 w-3 text-red-400" />}
                          {section.notes && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Active section content */}
                  {debrief.sections
                    .filter((s) => s.department === activeTab)
                    .map((section) => (
                      <SectionEditor
                        key={section.id}
                        debriefId={debrief.id}
                        section={section}
                        isPublished={debrief.status === 'published'}
                      />
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </AsyncBoundary>
    </div>
  )
}

function SectionEditor({
  debriefId,
  section,
  isPublished,
}: {
  debriefId: string
  section: DebriefSection
  isPublished: boolean
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    ratingOverall: section.ratingOverall,
    ratingVenue: section.ratingVenue,
    ratingSound: section.ratingSound,
    ratingLighting: section.ratingLighting,
    ratingLoadIn: section.ratingLoadIn,
    ratingCatering: section.ratingCatering,
    notes: section.notes ?? '',
    keyLearnings: section.keyLearnings ?? '',
    avoidNext: section.avoidNext ?? '',
    isConfidential: section.isConfidential,
  })

  const saveMutation = useMutation({
    mutationFn: () =>
      artistFetch(`/debriefs/${debriefId}/sections/${section.id}`, {
        method: 'PATCH',
        body: {
          ...form,
          notes: form.notes || null,
          keyLearnings: form.keyLearnings || null,
          avoidNext: form.avoidNext || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debriefs'] })
      setEditing(false)
    },
  })

  // Determine which ratings to show based on department
  const ratingFields = getRatingFieldsForDepartment(section.department)

  if (!editing) {
    return (
      <div className="px-4 py-4 space-y-3">
        {section.isConfidential && (
          <div className="flex items-center gap-1 text-xs text-red-600">
            <Lock className="h-3 w-3" /> Confidential - management eyes only
          </div>
        )}

        {/* Ratings */}
        <div className="flex flex-wrap gap-4">
          {ratingFields.map(({ key, label }) => (
            <div key={key}>
              <span className="text-xs text-ovation-muted">{label}</span>
              <div className="mt-0.5">
                <StarRatingDisplay value={(section as any)[key]} />
              </div>
            </div>
          ))}
        </div>

        {/* Notes */}
        {section.notes && (
          <div>
            <span className="text-xs font-medium text-ovation-muted">Notes</span>
            <p className="mt-1 text-sm whitespace-pre-wrap">{section.notes}</p>
          </div>
        )}
        {section.keyLearnings && (
          <div>
            <span className="text-xs font-medium text-green-700">What to repeat</span>
            <p className="mt-1 text-sm whitespace-pre-wrap">{section.keyLearnings}</p>
          </div>
        )}
        {section.avoidNext && (
          <div>
            <span className="text-xs font-medium text-red-700">What to avoid</span>
            <p className="mt-1 text-sm whitespace-pre-wrap">{section.avoidNext}</p>
          </div>
        )}

        {/* Action items */}
        <ActionItemsList debriefId={debriefId} section={section} isPublished={isPublished} />

        {!isPublished && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5"
          >
            Edit section
          </button>
        )}
      </div>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        saveMutation.mutate()
      }}
      className="px-4 py-4 space-y-4"
    >
      {/* Confidential toggle */}
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={form.isConfidential}
          onChange={(e) => setForm({ ...form, isConfidential: e.target.checked })}
        />
        <Lock className="h-3 w-3" /> Confidential (management only)
      </label>

      {/* Ratings */}
      <div className="flex flex-wrap gap-4">
        {ratingFields.map(({ key, label }) => (
          <div key={key}>
            <span className="text-xs text-ovation-muted">{label}</span>
            <StarRatingInput
              value={(form as any)[key]}
              onChange={(v) => setForm({ ...form, [key]: v })}
            />
          </div>
        ))}
      </div>

      {/* Text fields */}
      <div>
        <label className="mb-1 block text-xs text-ovation-muted">Notes</label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={4}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          placeholder="Department observations for this show..."
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-green-700">What to repeat</label>
          <textarea
            value={form.keyLearnings}
            onChange={(e) => setForm({ ...form, keyLearnings: e.target.value })}
            rows={3}
            className="w-full rounded-md border border-green-200 px-3 py-2 text-sm"
            placeholder="Things that went well..."
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-red-700">What to avoid</label>
          <textarea
            value={form.avoidNext}
            onChange={(e) => setForm({ ...form, avoidNext: e.target.value })}
            rows={3}
            className="w-full rounded-md border border-red-200 px-3 py-2 text-sm"
            placeholder="Things to change..."
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="rounded-md bg-ovation-ink px-4 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="rounded-md border border-ovation-ink/15 px-4 py-1.5 text-xs font-medium hover:bg-ovation-ink/5"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function ActionItemsList({
  debriefId,
  section,
  isPublished,
}: {
  debriefId: string
  section: DebriefSection
  isPublished: boolean
}) {
  const qc = useQueryClient()
  const [addingItem, setAddingItem] = useState(false)
  const [desc, setDesc] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [dueBy, setDueBy] = useState('')

  const addMutation = useMutation({
    mutationFn: () =>
      artistFetch(`/debriefs/${debriefId}/sections/${section.id}/actions`, {
        method: 'POST',
        body: {
          description: desc,
          ...(assignedTo ? { assignedTo } : {}),
          ...(dueBy ? { dueBy } : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debriefs'] })
      setDesc('')
      setAssignedTo('')
      setDueBy('')
      setAddingItem(false)
    },
  })

  const resolveMutation = useMutation({
    mutationFn: (itemId: string) =>
      artistFetch(`/debriefs/${debriefId}/actions/${itemId}/resolve`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debriefs'] }),
  })

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wider text-ovation-muted">
        Action Items ({section.actionItems.length})
      </p>
      {section.actionItems.length > 0 && (
        <div className="space-y-1">
          {section.actionItems.map((item) => (
            <div
              key={item.id}
              className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                item.resolved
                  ? 'border-green-200 bg-green-50/50 line-through opacity-60'
                  : 'border-ovation-ink/10 bg-white'
              }`}
            >
              {!item.resolved && !isPublished ? (
                <button
                  type="button"
                  onClick={() => resolveMutation.mutate(item.id)}
                  className="mt-0.5 shrink-0 rounded border border-ovation-ink/20 p-0.5 hover:bg-green-50"
                  title="Mark resolved"
                >
                  <CheckCircle className="h-3 w-3 text-ovation-muted" />
                </button>
              ) : item.resolved ? (
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              ) : null}
              <div className="flex-1">
                <span>{item.description}</span>
                {(item.assignedTo || item.dueBy) && (
                  <div className="mt-0.5 flex gap-3 text-xs text-ovation-muted">
                    {item.assignedTo && <span>Assigned: {item.assignedTo}</span>}
                    {item.dueBy && <span>Due: {item.dueBy}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isPublished && !addingItem && (
        <button
          type="button"
          onClick={() => setAddingItem(true)}
          className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900"
        >
          <Plus className="h-3 w-3" /> Add action item
        </button>
      )}

      {addingItem && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!desc) return
            addMutation.mutate()
          }}
          className="flex flex-wrap items-end gap-2 rounded-md border border-ovation-ink/10 p-2"
        >
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-xs text-ovation-muted">Description</label>
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm"
              placeholder="What needs to happen..."
            />
          </div>
          <div className="w-36">
            <label className="mb-1 block text-xs text-ovation-muted">Assigned to</label>
            <input
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm"
              placeholder="Person/role"
            />
          </div>
          <div className="w-36">
            <label className="mb-1 block text-xs text-ovation-muted">Due by</label>
            <input
              value={dueBy}
              onChange={(e) => setDueBy(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm"
              placeholder="Before next show"
            />
          </div>
          <button
            type="submit"
            disabled={addMutation.isPending}
            className="rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setAddingItem(false)}
            className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5"
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  )
}

function getRatingFieldsForDepartment(department: string): Array<{ key: string; label: string }> {
  const base = [{ key: 'ratingOverall', label: 'Overall' }]
  switch (department) {
    case 'sound':
      return [...base, { key: 'ratingSound', label: 'Sound Quality' }]
    case 'lighting':
      return [...base, { key: 'ratingLighting', label: 'Lighting' }]
    case 'venue_promoter':
      return [...base, { key: 'ratingVenue', label: 'Venue Cooperation' }]
    case 'logistics':
      return [...base, { key: 'ratingLoadIn', label: 'Load-in' }]
    case 'wardrobe_personal':
      return [...base, { key: 'ratingCatering', label: 'Catering' }]
    case 'production':
      return [
        ...base,
        { key: 'ratingVenue', label: 'Venue' },
        { key: 'ratingSound', label: 'Sound' },
        { key: 'ratingLighting', label: 'Lighting' },
        { key: 'ratingLoadIn', label: 'Load-in' },
      ]
    case 'management':
      return [
        ...base,
        { key: 'ratingVenue', label: 'Venue' },
      ]
    default:
      return base
  }
}

function AddDebriefForm({ onCreated }: { onCreated: () => void }) {
  const [showId, setShowId] = useState('')
  const [overallNotes, setOverallNotes] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const showsQuery = useQuery<Array<{ id: string; title: string; date: string; venue: string; city: string }>>({
    queryKey: ['shows'],
    queryFn: () => artistFetch('/shows'),
  })
  const shows = showsQuery.data ?? []

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await artistFetch('/debriefs', {
        method: 'POST',
        body: {
          showId,
          ...(overallNotes ? { overallNotes } : {}),
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show</label>
        <select
          required
          value={showId}
          onChange={(e) => setShowId(e.target.value)}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        >
          <option value="">Select a show...</option>
          {shows.map((s) => (
            <option key={s.id} value={s.id}>
              {new Date(s.date).toLocaleDateString()} — {s.title} ({s.venue}, {s.city})
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
          Overall Notes (optional)
        </label>
        <textarea
          value={overallNotes}
          onChange={(e) => setOverallNotes(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          placeholder="General show observations..."
        />
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        {submitting ? 'Creating...' : 'Create debrief'}
      </button>
      <p className="text-xs text-ovation-muted">
        This will create sections for all 10 departments. Each department head can then fill in their section.
      </p>
    </form>
  )
}
